import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import { triggerCustomEvent } from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

const searchState = new SearchStateManager();

export default class SearchFormController extends Controller {
  static targets = [
    'form',
    'input',
    'submit',
    'autocompleteSuggestions',
    'inlineFilters',
    'currentLabel',
  ];

  navigationKeyCodes = [
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'ArrowDown',
    'Enter',
  ];

  exclusionKeyCodes = [
    'MetaLeft',
    'MetaRight',
    'ControlLeft',
    'ControlRight',
    'AltLeft',
    'AltRight',
    'ShiftLeft',
    'ShiftRight',
  ];

  wrapperToggleClasses = [
    'ez-search-form__inline-filters',
    'ez-search-form__inline-filters__arrow',
    'ez-search-form__inline-filters__current',
  ];

  connect() {
    this.autocompleteUrl = this.inputTarget.dataset.autocompleteSource;

    // If not mobile/auto-focus search input & add autocomplete listener
    // --------------------------------------
    if (!this.isMobile()) {
      const searchVal = this.inputTarget.value;
      this.inputTarget.value = '';
      this.inputTarget.focus();
      this.inputTarget.value = searchVal;
    }

    let resetSearchState = true;
    if (document.body.classList.contains('resources-show')) {
      resetSearchState = false;
    }
    if (document.body.classList.contains('searches-show') && window.location.search.length > 0) {
      resetSearchState = false;
    }
    if (resetSearchState) searchState.resetStateToDefault();

    this.setTogglesFromState();
    this.checkAndToggleIsTyped();

    // Add is-ready class to search field, so we can apply styles after focused
    // (if we ever remove auto-focus, we need to change/re-think this)
    this.inputTarget.classList.add('is-ready');

    document.addEventListener('click', this.blurInlineFilters);

    if (this.resultsCount === '0') {
      triggerCustomEvent(events.search.without_results, { searchTerm: this.searchTerm });
    }

    if (this.pageNumber === '1' && this.searchType) {
      triggerCustomEvent(events.search.search_type, { searchType: this.searchType, searchTerm: this.searchTerm });
    }
  }

  disconnect() {
    document.removeEventListener('click', this.blurInlineFilters);
  }

  setTogglesFromState = () => {
    const activeTogglesFromState = searchState.getRawParamKeys();

    const allFiltersInputs = [
      ...this.element.querySelectorAll('.ez-input--checkbox input'),
    ];

    allFiltersInputs.forEach((checkbox) => {
      const checkboxFilterParam = checkbox.closest('.ez-input--checkbox').getAttribute('data-filter-param');

      if (activeTogglesFromState.includes(checkboxFilterParam)) {
        this.setActiveFilter(checkbox);
      }
    });
  }

  handleFilterClick = (event) => {
    event.stopPropagation();
    this.wrapperToggleClasses.forEach((wrapperClass) => {
      if (event.target.classList.contains(wrapperClass)) this.toggleInlineFilters();
    });
  }

  setActiveFilter = (inputEl) => {
    const allFilters = [...this.element.querySelectorAll('input[name=license]')];
    allFilters.forEach((el) => { el.checked = false; });

    this.currentLabelTarget.textContent = inputEl.closest('.ez-search-form__inline-filters__item').dataset.selected;
    this.inputTarget.placeholder = inputEl.closest('.ez-search-form__inline-filters__item').dataset.searchSelected;
    inputEl.checked = true;
  }

  handleFilterOptionClick = (event) => {
    this.setActiveFilter(event.target);
    searchState.updateParamKey(`${event.target.getAttribute('name')}-${event.target.getAttribute('value')}`);

    if (document.body.classList.contains('searches-show')) {
      this.submit(event);
      this.toggleInlineFilters();
    }

    if (window.innerWidth < 1000) {
      this.toggleInlineFilters();
    }
  }

  toggleInlineFilters = () => {
    this.inlineFiltersTarget.classList.toggle('is-open');
  }

  blurInlineFilters = (event) => {
    if (this.inlineFiltersTarget.classList.contains('is-open')
    && !event.target.closest('.ez-search-form__inline-filters')) {
      this.toggleInlineFilters();
    }
  }

  checkAndToggleIsTyped() {
    if (this.searchInputContents) {
      this.element.classList.add('is-typed');
    } else {
      this.element.classList.remove('is-typed');
    }
  }

  handleInputKeyUp = (event) => {
    if (!this.exclusionKeyCodes.includes(event.code) && !this.navigationKeyCodes.includes(event.code)) {
      this.checkAndToggleIsTyped();
    }

    if (this.navigationKeyCodes.includes(event.code) && document.querySelector('.ez-search-form__suggestions li')) {
      this.navigateSuggestions(event.code);
    } else if (!this.exclusionKeyCodes.includes(event.code) && this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    } else if (this.autocompleteSuggestionsTarget.innerHTML !== '') {
      this.resetAutocomplete();
    }
  }

  handleInputKeyDown = (event) => {
    if (event.metaKey && event.code === 'Enter') {
      this.submit(event);
    }
  }

  handleSuggestionClick = (event) => {
    this.inputTarget.value = event.target.textContent;
    this.submit(event);
  }

  navigateSuggestions = (keyCode) => {
    const suggestionsList = document.querySelector('.ez-search-form__suggestions');
    const activeSuggestion = suggestionsList.querySelector('li.is-active');

    const navUp = () => {
      if (activeSuggestion && activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.previousSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    const navDown = () => {
      if (activeSuggestion && activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.nextSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    if (keyCode === 'ArrowUp') {
      navUp();
    } else if (keyCode === 'ArrowDown') {
      navDown();
    } else if (keyCode === 'Enter') {
      this.resetAutocomplete();
    }
  }

  resetAutocomplete = () => {
    this.data.set('lastTerm', '');
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.remove('is-suggesting');
  }

  renderAutocomplete(res, req) {
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.add('is-suggesting');

    if (!res.length > 0) {
      this.resetAutocomplete();
      return;
    }

    Object.keys(res).forEach((i) => {
      const node = document.createElement('LI');

      if (res[i].indexOf(req) >= 0) {
        node.innerHTML = res[i].replace(req, `<strong>${req}</strong>`);
      } else {
        node.innerHTML = res[i];
      }

      node.dataset.action = 'mousedown->search-form#handleSuggestionClick';
      this.autocompleteSuggestionsTarget.appendChild(node);
    });
  }

  queryAutocomplete = (req) => {
    if (req === this.data.get('lastTerm')) return;
    this.data.set('lastTerm', this.inputTarget.value);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${this.autocompleteUrl}?term=${encodeURI(req)}`, true);
    xhr.setRequestHeader('Content-Type', 'text/plain');

    xhr.onload = () => {
      this.renderAutocomplete(JSON.parse(xhr.response), req);
    };

    xhr.send();
  }

  isMobile() {
    return document.body.classList.contains('is-mobile');
  }

  submitViaButton(event) {
    event.preventDefault();
    this.submit(event);
  }

  submit(event) {
    const searchedEventDetail = {
      term: this.inputTarget.value,
      searchRoute: this.inputTarget.getAttribute('data-search-route'),
      useSearchState: true,
    };

    if (event.metaKey && event.code && event.code === 'Enter') {
      searchedEventDetail.searchInNewTab = true;
    }

    Cookies.set('ezSearchType', 'internal');
    triggerCustomEvent('Searched', searchedEventDetail);
  }

  get resultsCount() {
    const searchInfo = document.querySelector('[data-search-results-count]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-results-count');
    }

    return null;
  }

  get pageNumber() {
    const queryString = window.location.search.slice(1);
    const params = new URLSearchParams(queryString);

    if (params.has('page')) {
      return params.get('page');
    }

    return '1';
  }

  get searchTerm() {
    return this.element.getAttribute('data-search-term').replace(/ /g, '-');
  }

  get searchType() {
    return this.element.getAttribute('data-search-type');
  }

  get searchInputContents() {
    return this.inputTarget.value;
  }
}
